import { Iterable } from 'immutable';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TDShapeType, TLDR, Tldraw } from '@tldraw/tldraw';
import { GradientLoadingIndicator } from 'edrolo-style-guide';
import { useImage } from 'edrolo/utils/ImageUtils';
import { DEFAULT_CANVAS_HEIGHT } from 'core/constants';
import { useAssetUploader } from 'core/utils';
import style from './DrawingInputField.scss';
import PT from 'prop-types';

export const DrawingInputField = ({
  tldrawDocument,
  backgroundImageURL,
  onPersist,
  readOnly,
}) => {
  const [app, setApp] = useState(null);
  const [backgroundImage, backgroundImageIsLoading] =
    useImage(backgroundImageURL);

  const tldrawContainerRef = useRef(null);
  const { assetUploadHandler } = useAssetUploader();

  const [widgetWidth, setWidgetWidth] = useState();

  useEffect(() => {
    const { clientWidth } = tldrawContainerRef.current || {};
    if (clientWidth) {
      setWidgetWidth(clientWidth);
    }
  }, [tldrawContainerRef]);

  /*
   * Insert the image we'll be drawing over onto the canvas and re-orient the
   * viewport so it fills the dimensions of the widget and the image is centered.
   */
  useEffect(() => {
    if (!backgroundImage || !app) {
      return;
    }

    const imageWidth = backgroundImage.width;
    const imageHeight = backgroundImage.height;

    app.document.assets = {
      ['background-image-asset']: {
        id: 'background-image-asset',
        src: backgroundImage.src,
        size: [imageWidth, imageHeight],
      },
    };

    app.createShapes({
      id: 'background-image',
      name: 'Background Image',
      type: TDShapeType.Image,
      point: [0, 0],
      size: [imageWidth, imageHeight],
      isLocked: true,
      assetId: 'background-image-asset',
    });

    app.moveToBack(['background-image']);
    app.zoomToFit();
    app.selectNone();

    // clear the actions taken above from the undo/redo stack
    app.history = [];
  }, [backgroundImage, app]);

  const handleMount = useCallback(
    (app) => {
      setApp(app);

      if (tldrawDocument) {
        app.mergeDocument(
          Iterable.isIterable(tldrawDocument)
            ? tldrawDocument.toJS()
            : tldrawDocument
        );
      }

      if (!tldrawContainerRef.current) {
        return;
      }

      // update viewport bounds to match the dimensions of the widget
      // required for API calls to zoom/pan methods to function correctly
      app.updateBounds({
        minX: 0,
        minY: 0,
        maxX: widgetWidth,
        maxY: DEFAULT_CANVAS_HEIGHT,
        width: widgetWidth,
        height: DEFAULT_CANVAS_HEIGHT,
      });

      app.zoomToFit();
    },
    [tldrawDocument, widgetWidth]
  );

  const handlePersist = useCallback(
    async (app) => {
      let tldrawDocumentSVG = null;
      if (app) {
        const shapeIds = app.getShapes('page').map((shape) => shape.id);
        if (shapeIds.length > 0) {
          const svg = await app.getSvg(shapeIds, {
            includeFonts: true,
            transparentBackground: true,
          });
          tldrawDocumentSVG = TLDR.getSvgString(svg, 1);
        }
      }
      onPersist(app.document, tldrawDocumentSVG);
    },
    [onPersist]
  );

  const handleAssetCreate = useCallback(
    async (tlDrawApp, file, assetId) => {
      // Callback with signature defined by TLDraw:
      // (app: TldrawApp, file: File, assetId: string) => Promise<string | false>
      // Should return the value for the image/video's `src` property,
      // or false on failure.

      // Example assetId: 22335816-5cd2-499d-23d2-ed5fd377bdf3
      try {
        // The `assetUploadHandler` function from useAssetUploader via AssetUploadContext
        // should have a signature of (file: File, assetId: string) (no TldrawApp)
        const asset = await assetUploadHandler(file, assetId);
        return asset.url;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    [assetUploadHandler]
  );

  if (backgroundImageIsLoading) {
    return <GradientLoadingIndicator />;
  }

  // HACK: hide the help button that's displayed by tldraw in the lower right corner
  let helpButton =
    document.getElementsByClassName('c-loqjux').length === 1
      ? document.getElementsByClassName('c-loqjux')[0]
      : null;
  if (helpButton) {
    helpButton.hidden = true;
  }

  return (
    <div className={style.tldrawBorderWrapper}>
      <div
        key={widgetWidth} // re-render on widgetWidth change to update app.bounds
        ref={tldrawContainerRef}
        className={style.tldrawAppContainer}
        style={{ height: `${DEFAULT_CANVAS_HEIGHT}px` }}
      >
        <Tldraw
          // readOnly={readOnly}  // HACK: readOnly doesn't work
          showStyles={!readOnly} // HACK: readOnly doesn't work
          showTools={!readOnly} // HACK: readOnly doesn't work
          showUI={!readOnly} // HACK: readOnly doesn't work
          showMenu={false}
          showPages={false}
          showSponsorLink={false}
          onMount={handleMount}
          onPersist={handlePersist}
          onAssetCreate={handleAssetCreate}
        />
      </div>
    </div>
  );
};

DrawingInputField.propTypes = {
  tldrawDocument: PT.any, // tldraw doc
  backgroundImageURL: PT.string,
  onPersist: PT.func.isRequired,
  readOnly: PT.bool,
};
